import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Bell, BellOff, BellRing } from 'lucide-react';
import { Button } from './ui/button';
import { useWallet } from '@solana/wallet-adapter-react';
import { toast } from 'sonner';
import { getServiceWorkerRegistration } from '../serviceWorkerRegistration';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/** URL-safe base64 → Uint8Array (Web Push spec for applicationServerKey). */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

const isIOS = () => {
  const ua = navigator.userAgent || '';
  return /iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
};

/**
 * Push notification opt-in/out button.
 *
 * State machine:
 *  - "unsupported": browser/context can't do Web Push → button hidden
 *  - "ios-needs-install": iOS Safari but app NOT installed to home screen
 *      → button shown but click explains the requirement
 *  - "ready": we can subscribe; click triggers permission + subscribe
 *  - "enabled": already subscribed → click unsubscribes
 */
const EnableNotificationsButton = () => {
  const { connected, publicKey } = useWallet();
  const [state, setState] = useState('checking');
  const [busy, setBusy] = useState(false);

  // Determine initial state
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!('serviceWorker' in navigator) || !('PushManager' in window) || !window.isSecureContext) {
        if (!cancelled) setState('unsupported');
        return;
      }
      // iOS Safari only allows web push for apps installed to home screen
      if (isIOS() && !isStandalone()) {
        if (!cancelled) setState('ios-needs-install');
        return;
      }
      try {
        const reg = await getServiceWorkerRegistration();
        if (!reg) {
          if (!cancelled) setState('unsupported');
          return;
        }
        const sub = await reg.pushManager.getSubscription();
        if (!cancelled) setState(sub ? 'enabled' : 'ready');
      } catch (e) {
        if (!cancelled) setState('unsupported');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleEnable = async () => {
    if (state === 'ios-needs-install') {
      toast.info("On iOS, install Metal Lego to your home screen first — then re-open it from the home screen icon and tap this button again.", { duration: 6500 });
      return;
    }
    if (!connected || !publicKey) {
      toast.error("Connect your wallet first — notifications are tied to your wallet so we know who to ping.");
      return;
    }
    setBusy(true);
    try {
      // Make sure SW is registered + active
      const reg = await getServiceWorkerRegistration();
      if (!reg) {
        toast.error("Service worker not available. Try reloading the page over HTTPS.");
        return;
      }
      // Permission
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') {
        toast.error("Notifications permission denied. You can re-enable it in browser settings.");
        return;
      }
      // Fetch VAPID public key + subscribe
      const keyRes = await axios.get(`${BACKEND_URL}/api/push/vapid-public-key`);
      const applicationServerKey = urlBase64ToUint8Array(keyRes.data.public_key);
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey });
      const subJson = sub.toJSON();
      await axios.post(`${BACKEND_URL}/api/push/subscribe`, {
        wallet_address: publicKey.toString(),
        endpoint: subJson.endpoint,
        keys: subJson.keys,
        user_agent: navigator.userAgent,
      });
      setState('enabled');
      toast.success("Notifications enabled — we'll ping you when someone beats your high score.");
    } catch (e) {
      console.error('[push] enable failed', e);
      toast.error(e?.response?.data?.detail || 'Could not enable notifications. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async () => {
    setBusy(true);
    try {
      const reg = await getServiceWorkerRegistration();
      if (!reg) return;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const endpoint = sub.endpoint;
        await sub.unsubscribe();
        try {
          await axios.post(`${BACKEND_URL}/api/push/unsubscribe`, { endpoint });
        } catch (_) { /* server cleanup best-effort */ }
      }
      setState('ready');
      toast.success("Notifications disabled.");
    } catch (e) {
      console.error('[push] disable failed', e);
      toast.error('Could not disable notifications.');
    } finally {
      setBusy(false);
    }
  };

  if (state === 'checking' || state === 'unsupported') return null;

  if (state === 'enabled') {
    return (
      <Button
        onClick={handleDisable}
        disabled={busy}
        variant="outline"
        size="sm"
        className="border-[#FFD700]/60 text-[#FFD700] hover:bg-[#FFD700]/10 hover:text-[#FFD700] gap-2 font-game text-[10px]"
        data-testid="push-notifications-btn"
        title="Notifications enabled — click to disable"
      >
        <BellRing className="w-3.5 h-3.5" />
        ALERTS ON
      </Button>
    );
  }
  // 'ready' or 'ios-needs-install'
  const label = state === 'ios-needs-install' ? 'INSTALL FIRST' : 'ENABLE ALERTS';
  return (
    <Button
      onClick={handleEnable}
      disabled={busy}
      variant="outline"
      size="sm"
      className="border-[#9945FF]/60 text-[#E0CCFF] hover:bg-[#9945FF]/10 hover:text-white gap-2 font-game text-[10px]"
      data-testid="push-notifications-btn"
      title={state === 'ios-needs-install'
        ? 'On iOS, web push only works after installing the app to your home screen'
        : 'Enable push notifications'}
    >
      {state === 'ios-needs-install' ? <BellOff className="w-3.5 h-3.5" /> : <Bell className="w-3.5 h-3.5" />}
      {label}
    </Button>
  );
};

export default EnableNotificationsButton;
